import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { toast } from 'sonner';
import Honeybadger from '@honeybadger-io/js';

import { deserializer } from '../../../../../../shared/utils/deserializer';
import { usePagination, useSearchBar } from '../../../../../shared/hooks';
import { fetchReviewRequests as apiFetchReviewRequests } from '../../services/reviewRequestService';
import ReviewRequestContext from './ReviewRequestContext';

const ReviewRequestProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [reviewsRequested, setReviewsRequested] = useState([]);

  const { handlePageChange, pagination, updatePagination } = usePagination();
  const { handleSearchChange, searchInput, searchQuery } = useSearchBar();

  const fetchReviewsRequested = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await apiFetchReviewRequests({
        clientName: searchQuery,
        page: pagination.currentPage,
        perPage: pagination.perPage,
      });

      const reviewRequests = await deserializer.deserialize(response);

      setReviewsRequested(reviewRequests);
      updatePagination(response.meta.pagination);
    } catch (error) {
      toast.error(error.message);
      Honeybadger.notify(error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.perPage, searchQuery]);

  useEffect(() => {
    fetchReviewsRequested();
  }, [fetchReviewsRequested]);

  const contextValue = useMemo(
    () => ({
      fetchReviewsRequested,
      handlePageChange,
      handleSearchChange,
      isLoading,
      pagination,
      reviewsRequested,
      searchInput,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchReviewsRequested, isLoading, pagination, reviewsRequested, searchInput]
  );

  return (
    <ReviewRequestContext.Provider value={contextValue}>
      {children}
    </ReviewRequestContext.Provider>
  );
};

ReviewRequestProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ReviewRequestProvider;
